import {
  Badge,
  Button,
  Column,
  Loader,
  Message,
  Row, Spacer,
  Spinner, Table,
  Text,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { RootState } from 'client/store/configureStore';
import { DesignTypes } from 'common/enums/hierarchy-enums';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { THierarchyCountry, THierarchyDesign, THierarchyTree } from 'common/interfaces/hierarchy';
import { uniqBy } from 'es-toolkit/compat';
import { getUnpackState } from '../../store/entities/object/selectors';

enum FilterMap {
  Name = 'name',
  Well = 'well',
  Site = 'site',
  Wellbore = 'wellbore',
  Project = 'project',
  Country = 'country',
  Status = 'status',
}

type TFilters = {
  [k in FilterMap]?: string
};

const getDesignOptions = (t: TFunction) => [
  { label: t(translations.edm_wellActual), value: DesignTypes.Actual },
  { label: t(translations.edm_wellPrototype), value: DesignTypes.Prototype },
  { label: t(translations.edm_wellPlanned), value: DesignTypes.Planned },
];

// Takes pre unpacked hierrachy object from the server and and flats to to table representation
const prepareHierarchy = (hierarchyObject: THierarchyTree): THierarchyDesign[] => {
  if (!Object.keys(hierarchyObject)?.length) {
    return [];
  }
  return uniqBy(Object.values(hierarchyObject).reduce((acc: THierarchyDesign[][][][][][], cur: THierarchyCountry) => {
    return [...acc, cur.projects?.map(p => p.sites?.map(s => s.wells?.map(w => w.wellbores?.map(wb => wb?.designs))))];
  }, []).flat(5).filter(Boolean), 'metadata.scenario_id');
};

type DeisgnSelectorProps = {
  onClose: () => void,
  handleConfirmNextStep: (selectedDesingIds: string[]) => void,
  unPackState: any
};

const getHeaders = (
  onFilter: (event: React.ChangeEvent<HTMLInputElement>) => void,
  filters: TFilters,
  t: TFunction,
  onToggleAll: () => void,
  allChecked: boolean,
) => {
  const translatedSearch = t(translations.search);
  return [
    {
      cells: [
        {
          onChange: onToggleAll,
          type: 'CheckBox',
          checked: allChecked,
        },
        {
          value: `${t(translations.design)} ${t(translations.name)}`,
        },
        {
          value: t(translations.wellbore),
        },
        {
          value: t(translations.well),
        },
        {
          value: t(translations.site),
        },
        {
          value: t(translations.field),
        },
        {
          value: t(translations.country),
        },
        {
          value: t(translations.status),
        },
      ],
    },
    {
      cells: [
        {
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.name,
          name: FilterMap.Name,
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.well,
          name: FilterMap.Well,
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.wellbore,
          name: FilterMap.Wellbore,
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.site,
          name: FilterMap.Site,
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.project,
          name: FilterMap.Project,
        },
        {
          onChange: onFilter,
          placeholder: translatedSearch,
          type: 'Input',
          value: filters.country,
          name: FilterMap.Country,
        },
        {
          onChange: onFilter,
          options: getDesignOptions(t),
          placeholder: 'Input',
          type: 'Select',
          clearable: true,
          value: filters.status,
          name: FilterMap.Status,
        },
      ],
    },
  ];
};

const DesignSelector = ({
  onClose,
  handleConfirmNextStep,
  unPackState: {
    preUnpackData, unPackError, isLoadingPreUnpack,
  },
}: DeisgnSelectorProps) => {
  const { t } = useTranslation();

  const tableData = prepareHierarchy(preUnpackData);

  const [filters, setFilter] = useState<TFilters>({});
  const [selectedDesigns, setSelectedDesigns] = useState<{ [k: string]: boolean }>({});

  const selectedDesingIds = Object.keys(selectedDesigns);

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === FilterMap.Status && !value) {
      const { status, ...restFilters } = filters;
      return setFilter({ ...restFilters });
    }
    setFilter({ ...filters, [name]: value });
  };

  const onSelectDesign = (designId: string) => {
    const present = selectedDesigns[designId];
    if (present) {
      const { [designId]: without, ...restSelecterDesings } = selectedDesigns;
      setSelectedDesigns(restSelecterDesings);
    } else {
      setSelectedDesigns({ ...selectedDesigns, [designId]: true });
    }
  };

  const handeDeselectAll = () => {
    setSelectedDesigns({});
  };

  const handleSelectAll = () => {
    const holder: { [k: string]: boolean } = {};
    tableData.forEach(i => {
      holder[i.metadata.scenario_id] = true;
    });
    setSelectedDesigns(holder);
  };

  const allSelected = tableData.length > 0
    && tableData.every(i => selectedDesigns[i.metadata.scenario_id]);

  const onToggleAll = () => allSelected ? handeDeselectAll() : handleSelectAll();

  const headers = getHeaders(onFilter, filters!, t, onToggleAll, allSelected);

  const filterData = ({ cells }: any): boolean => {
    let match = true;
    for (const filter in filters) {
      const row = cells.find((c: { name: string; }) => c.name === filter);
      const v = row!.selectValue || row!.value;
      // @ts-ignore
      match = v.toLowerCase().includes(filters[filter]?.toLowerCase());
      if (!match) {
        break;
      }
    }
    return match;
  };

  const getColor = (status: string) => {
    const colorMap = {
      ACTUAL: '#4CB34C',
      PLANNED: '#EC6F37',
      PROTOTYPE: '#ACAAAA',
    };
    return colorMap[status as keyof typeof colorMap];
  };

  const rows = tableData.map(i => ({
    cells: [
      {
        onChange: () => onSelectDesign(i.metadata.scenario_id),
        type: 'CheckBox',
        checked: selectedDesigns[i.metadata.scenario_id],
      },
      { value: i.name, name: FilterMap.Name },
      { value: i.metadata.wellbore, name: FilterMap.Wellbore },
      { value: i.metadata.wells, name: FilterMap.Well },
      { value: i.metadata.site, name: FilterMap.Site },
      { value: i.metadata.project, name: FilterMap.Project },
      { value: i.metadata.country, name: FilterMap.Country },
      {
        value: <Badge
          color={getColor(i.metadata.status)}
          // @ts-ignore
          title={(
            <span style={{ color: '#FFF' }}>
              {i.metadata.status}
              {' '}
            </span>
)}
          small
        />,
        name: FilterMap.Status,
        selectValue: i.metadata.status,
      },
    ],
  })).filter(filterData);

  const renderTable = () => {
    return (
      <Table
        table={{
          headers,
          rows,
        }}
      />
    );
  };

  if (unPackError) {
    return <Message message={unPackError} />;
  }

  return isLoadingPreUnpack ? <Loader text={t(translations.edm_parsing)!} width="100%" height="50px" theme="light"><Spinner dark /></Loader>
    : (
      <>
        <Row>
          <Column>
            <Text muted>
              {tableData.length}
              {' '}
              {t(translations.edm_foundDesigns)}
              {' '}
            </Text>
          </Column>
        </Row>
        <Spacer />
        <Row>
          {renderTable()}
        </Row>
        <Spacer />
        <Row>
          <Button
            label={`${t(translations.edm_confirmDesigns)} ${selectedDesingIds.length}`}
            onClick={() => {
              handleConfirmNextStep(selectedDesingIds);
              onClose();
            }}
            colored
            disabled={!selectedDesingIds.length}
          />
          <Button label={t(translations.cancel)} onClick={onClose} />
        </Row>
      </>
    );
};

const mapStateToProps = (state: RootState) => ({
  unPackState: getUnpackState(state),
});

export default connect(
  mapStateToProps,
  null,
)(DesignSelector);
